import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, currencyString, truncateString } from "@qgiv/core-js";
import { getFormattedPrice } from "@qgiv/core-donor";
import { selectSpecificFormSetting } from "../../../../../redux/slices/formSettingsSlice";
import { selectRegistrationFeeDisplaySettings } from "../../../../../redux/slices/p2pSettingsSlice";
import { selectAllCategories } from "../../../../../redux/slices/categoriesSlice";
import { selectAllRegistration } from "../../../../../redux/slices/registrationSlice";
import ParticipantSummaryCategoryPrice from "./ParticipantSummaryCategoryPrice";

const ConnectedParticipantSummaryCategoryPrice = ({
    categoryId,
    registrationIndex,
}) => {
    const {
        ENUMS: {
            RegistrationFeeDisplay: { DISPLAY_NOTHING },
        },
    } = constants;

    const currency = useSelector((state) =>
        selectSpecificFormSetting(state, "currency"),
    );
    const { registrationFeeDisplay, registrationFeeDisplayText } = useSelector(
        selectRegistrationFeeDisplaySettings,
    );
    const categories = useSelector(selectAllCategories);
    const category = categories.find(({ id }) => `${id}` === categoryId);
    const { earlyRegistration, fee, registrationFee } = category;
    const { promoDiscounts = {} } = useSelector(selectAllRegistration);
    const { participants = [] } = promoDiscounts;

    // Promo Logic to determine if participant has promo then set values
    let hasPromo;
    let promoDiscount;
    let promoCodeApplied;

    if (participants) {
        const participant = participants.find(
            (e) => Number(e.id) === Number(registrationIndex),
        );

        if (
            participant &&
            participant.promoCodes &&
            participant.originalCategoryFee > 0
        ) {
            promoDiscount = participant.postPromoCategoryFee;

            // eslint-disable-next-line array-callback-return, consistent-return
            promoCodeApplied = participant.promoCodes.map((promo, i) => {
                if (promo.success) {
                    hasPromo = true;
                    if (
                        participant.promoCodes.length > 1 &&
                        i !== participant.promoCodes.length - 1
                    ) {
                        // If more than one separate with space
                        return `${truncateString(promo.name, 12)}, `;
                    }
                    return truncateString(promo.name, 12);
                }
            });
        }
    }
    // Infer props from settings
    const isFree = registrationFee === 0;
    // Helper func to get either a currency formatted number
    // or a custom string of text from p2p settings.
    const formattedPrice = getFormattedPrice(
        registrationFee,
        currency,
        registrationFeeDisplay,
        registrationFeeDisplayText,
    );

    const isVisible =
        registrationFee > 0 || fee > 0
            ? true
            : registrationFeeDisplay !== DISPLAY_NOTHING;
    const formattedDiscountedPrice = currencyString(fee, currency);
    const hasDiscount = isFree ? false : earlyRegistration;

    const formattedPromoPrice = getFormattedPrice(
        promoDiscount,
        currency,
        registrationFeeDisplay,
        registrationFeeDisplayText,
        hasPromo,
    );

    const participantSummaryCategoryPriceProps = {
        formattedPrice,
        formattedDiscountedPrice,
        formattedPromoPrice,
        hasDiscount,
        hasPromo,
        isVisible,
        promoCodeApplied,
    };

    return (
        <ParticipantSummaryCategoryPrice
            {...participantSummaryCategoryPriceProps}
        />
    );
};

ConnectedParticipantSummaryCategoryPrice.propTypes = {
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    registrationIndex: PropTypes.number.isRequired,
};

export default ConnectedParticipantSummaryCategoryPrice;
